import { useId } from 'react'

import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import type { WarehousePickVm } from '../model/types'

/**
 * Общий выпадающий список складов базы (поставки, инвентаризация, ценники):
 * склады из структуры 1С, разложенные по организации и подразделению. Подпись
 * варианта — «склад · организация», как раньше; группа поясняет, чей склад.
 * Значение вне справочника (склад поставки, которого уже нет) показывается
 * отдельной строкой `extra`, а не теряется.
 *
 * Склады читаются из 1С на лету (RP-LSB2): пока ответа нет, список закрыт и
 * показывает только `extra` — то, что выбрано, как оно известно без 1С. 1С не
 * ответила — вместо списка её текст и «Повторить», а не прежний или пустой
 * список; складов в базе нет — так и сказано.
 */
export function WarehouseSelect({
  label,
  value,
  pick,
  onChange,
  onRetry,
  placeholder,
  extra,
  disabled = false,
}: {
  label: string
  value: string
  pick: WarehousePickVm
  onChange: (value: string) => void
  /** «Повторить» после отказа 1С: перечитать склады этой базы. */
  onRetry: () => void
  placeholder?: string
  extra?: { value: string; label: string } | null
  disabled?: boolean
}) {
  const id = useId()
  const status = pick.query.status
  if (status !== 'ready' && status !== 'loading' && status !== 'idle') {
    return (
      <div className="krp-field" role="group" aria-labelledby={id}>
        <span className="krp-field__label" id={id}>
          {label}
        </span>
        {extra !== undefined && extra !== null && <p className="onec-field__note">Сейчас: {extra.label}</p>}
        <OneCQueryState query={pick.query} emptyText="В этой базе 1С нет складов" onRetry={onRetry} />
      </div>
    )
  }
  if (status !== 'ready') {
    // До ответа 1С выбирать не из чего: виден только выбранный склад (как он известен без 1С) или что идёт чтение.
    const known = extra !== undefined && extra !== null
    return (
      <div className="krp-field">
        <label className="krp-field__label" htmlFor={id}>
          {label}
        </label>
        <select id={id} value={known ? extra.value : value} disabled aria-busy={status === 'loading' || undefined} onChange={() => undefined}>
          {known ? (
            <option value={extra.value}>{extra.label}</option>
          ) : (
            <option value={value}>{status === 'loading' ? 'Склады читаются из 1С…' : (placeholder ?? '')}</option>
          )}
        </select>
      </div>
    )
  }
  // Одна группа без подразделения ничего не поясняет — тогда список плоский.
  const flat = pick.groups.length <= 1
  return (
    <div className="krp-field">
      <label className="krp-field__label" htmlFor={id}>
        {label}
      </label>
      <select id={id} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {extra !== undefined && extra !== null && <option value={extra.value}>{extra.label}</option>}
        {flat
          ? pick.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))
          : pick.groups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))}
      </select>
    </div>
  )
}
